import type { ReactEventHandler, ReactNode } from "react";
import { Box, Dialog, SwipeableDrawer } from "@mui/material";
import type { DialogProps } from "@mui/material/Dialog";
import BottomSheetHandle from "shared/ui/BottomSheetHandle";
import {
    bottomSheetContentSx,
    bottomSheetPaperSx,
} from "shared/ui/bottomSheetStyles";
import { useBreakpoint } from "shared/hooks/useBreakpoint";

export type ResponsiveDialogProps = DialogProps;

function mergePaperSx(
    base: Record<string, unknown>,
    ...sources: Array<Record<string, unknown> | undefined>
) {
    return sources.reduce<Record<string, unknown>>((acc, sx) => ({ ...acc, ...sx }), base);
}

export default function ResponsiveDialog({
    open,
    onClose,
    children,
    PaperProps,
    slotProps,
    keepMounted,
    ...dialogProps
}: ResponsiveDialogProps) {
    const { isDesktop } = useBreakpoint();

    if (isDesktop) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                PaperProps={PaperProps}
                slotProps={slotProps}
                keepMounted={keepMounted}
                {...dialogProps}
            >
                {children}
            </Dialog>
        );
    }

    const paperSlot = slotProps?.paper;
    const paperSlotSx =
        paperSlot && typeof paperSlot === "object" && "sx" in paperSlot
            ? (paperSlot.sx as Record<string, unknown> | undefined)
            : undefined;
    const legacyPaperSx = PaperProps?.sx as Record<string, unknown> | undefined;

    const mobilePaperSx = mergePaperSx(
        bottomSheetPaperSx as Record<string, unknown>,
        paperSlotSx,
        legacyPaperSx,
    );

    const handleDrawerClose: ReactEventHandler = (event) => {
        onClose?.(event, "backdropClick");
    };

    return (
        <SwipeableDrawer
            anchor="bottom"
            open={Boolean(open)}
            onClose={handleDrawerClose}
            onOpen={() => {}}
            disableSwipeToOpen
            keepMounted={keepMounted}
            slotProps={{
                ...slotProps,
                paper: {
                    ...(typeof paperSlot === "object" ? paperSlot : {}),
                    elevation: 8,
                    sx: mobilePaperSx,
                },
            }}
        >
            <BottomSheetHandle />
            <Box sx={bottomSheetContentSx}>{children as ReactNode}</Box>
        </SwipeableDrawer>
    );
}
